import { useEffect, useRef, useState } from "react";
import { useArenaStore } from "../../stores/useArenaStore";
import { getSceneHandle } from "../../lib/sceneRef";
import type { ArenaState } from "@shared/types";

interface DamageNumbersProps {
  state: ArenaState;
}

interface FloatingNum {
  key: string;
  x: number;
  y: number;
  amount: number;
  crit: boolean;
  /** true if this number is damage the self player dealt (highlight) */
  bySelf: boolean;
}

interface KillPop {
  key: string;
  text: string;
  color: string;
}

export function DamageNumbers({ state }: DamageNumbersProps) {
  const { playerId } = useArenaStore();
  const [nums, setNums] = useState<FloatingNum[]>([]);
  const [vignetteKey, setVignetteKey] = useState<string | null>(null);
  const [killPop, setKillPop] = useState<KillPop | null>(null);
  const seenRef = useRef<Set<string>>(new Set());
  const cleanupRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    const scene = getSceneHandle();
    if (!scene) return;

    const freshNums: FloatingNum[] = [];
    let freshVignette: string | null = null;
    let freshKill: KillPop | null = null;

    for (const ev of state.events) {
      if (seenRef.current.has(ev.id)) continue;
      seenRef.current.add(ev.id);
      if (seenRef.current.size > 300) {
        seenRef.current = new Set([...seenRef.current].slice(-150));
      }

      if (ev.kind === "damage") {
        const d = ev.data as {
          sourceId: string;
          targetId: string;
          amount: number;
          crit: boolean;
          pos: { x: number; z: number };
        };
        const screen = scene.projectToScreen(d.pos.x, d.pos.z);
        if (!screen) continue;
        freshNums.push({
          key: ev.id,
          x: screen.x,
          y: screen.y,
          amount: d.amount,
          crit: d.crit,
          bySelf: d.sourceId === playerId,
        });
        // self took damage -> vignette flash
        if (d.targetId === playerId) {
          freshVignette = ev.id;
        }
      } else if (ev.kind === "kill") {
        const d = ev.data as {
          killerId: string;
          victimKind: string;
          victimName: string;
        };
        // self got the kill -> center pop
        if (d.killerId === playerId) {
          freshKill =
            d.victimKind === "player"
              ? { key: ev.id, text: "☠ ELIMINATION", color: "text-red-400" }
              : { key: ev.id, text: `🏹 Slew a ${d.victimName}`, color: "text-dawn-400" };
        }
      }
    }

    if (freshNums.length > 0) {
      setNums((prev) => [...prev, ...freshNums].slice(-24));
      // auto-remove after the animation completes
      const toRemove = freshNums.map((n) => n.key);
      const t = setTimeout(() => {
        setNums((prev) => prev.filter((n) => !toRemove.includes(n.key)));
      }, 1200);
      // store cleanup on the ref so we don't leak timers
      cleanupRef.current.push(t);
    }
    if (freshVignette) {
      setVignetteKey(freshVignette);
      const t = setTimeout(() => setVignetteKey((k) => (k === freshVignette ? null : k)), 550);
      cleanupRef.current.push(t);
    }
    if (freshKill) {
      setKillPop(freshKill);
      const t = setTimeout(() => setKillPop((k) => (k?.key === freshKill.key ? null : k)), 1500);
      cleanupRef.current.push(t);
    }
  }, [state.events, playerId]);

  useEffect(() => {
    const timers = cleanupRef.current;
    return () => {
      timers.forEach(clearTimeout);
    };
  }, []);

  return (
    <>
      {/* floating damage numbers */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        {nums.map((n) => (
          <div
            key={n.key}
            className={`absolute font-black ${n.crit ? "dmg-crit" : "dmg-number"}`}
            style={{
              left: n.x,
              top: n.y,
              fontSize: n.crit ? 28 : n.bySelf ? 20 : 16,
              color: n.crit ? "#facc15" : n.bySelf ? "#ffffff" : "#e5e7eb",
              zIndex: 20,
            }}
          >
            {n.crit && (
              <span className="mr-1 text-xs align-middle" style={{ color: "#facc15" }}>
                CRIT
              </span>
            )}
            {n.amount}
          </div>
        ))}
      </div>

      {/* self-hit vignette */}
      {vignetteKey && (
        <div key={vignetteKey} className="hit-vignette pointer-events-none absolute inset-0 z-10" />
      )}

      {/* kill confirmation pop */}
      {killPop && (
        <div
          key={killPop.key}
          className={`kill-pop pointer-events-none absolute left-1/2 top-[38%] z-20 text-3xl font-black tracking-wide ${killPop.color}`}
          style={{ textShadow: "0 2px 8px rgba(0,0,0,0.9)" }}
        >
          {killPop.text}
        </div>
      )}
    </>
  );
}
